import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, map, catchError, throwError } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

/* ============================== Types ============================== */

export interface WizardStatusDto {
  wizardId: string | null;
  completedVersion: number | null;
  latestVersion: number | null;
  completed: boolean;
  completedAt: string | null;
  freezeUntil: string | null;
}

/** DTO renvoyé par /defaults (tolère payloadDefault OU payload) */
export interface WizardDefaultsDto {
  wizardId: string;
  version: number | null;       // ✅ correspond à la version assignée si dispo
  label?: string | null;
  payloadDefault?: any;
  payload?: any;
}

/* ============================== Service ============================== */

@Injectable({ providedIn: 'root' })
export class WizardService {
  private http = inject(HttpClient);

  /** Base d’URL injectée dynamiquement selon le domaine (local/dev/prod). */
  private baseUrl = inject(API_BASE_URL);
  private base = `${this.baseUrl}/api/wizard`;

  private storagePrefix = 'wt_wizard_est_';

  getDefaults(establishmentId: string): Observable<WizardDefaultsDto> {
    const url = `${this.base}/establishments/${encodeURIComponent(establishmentId)}/defaults`;
    return this.http.get<WizardDefaultsDto>(url).pipe(
      map((raw) => this.normalizeDefaults(raw)),
      catchError((err) => {
        const empty: WizardDefaultsDto = {
          wizardId: 'establishment-setup',
          version: null,
          label: null,
          payloadDefault: {},
        };
        if (err?.status === 404) return of(empty);
        return throwError(() => err);
      })
    );
  }

  getEstablishmentStatus(establishmentId: string): Observable<WizardStatusDto | null> {
    const url = `${this.base}/establishments/${encodeURIComponent(establishmentId)}/status`;
    return this.http.get<WizardStatusDto>(url).pipe(
      map((s) => s ?? null),
      catchError((err) => {
        if (err?.status === 404) return of(null);
        return throwError(() => err);
      })
    );
  }

  completeEstablishment(establishmentId: string, version?: number | null): Observable<void> {
    const url = `${this.base}/establishments/${encodeURIComponent(establishmentId)}/complete`;
    let params = new HttpParams();
    if (version != null) params = params.set('version', String(version));
    return this.http.post<void>(url, null, { params });
  }

  freezeEstablishment(establishmentId: string, untilIso: string): Observable<void> {
    const url = `${this.base}/establishments/${encodeURIComponent(establishmentId)}/freeze`;
    const params = new HttpParams().set('until', untilIso);
    return this.http.post<void>(url, null, { params });
  }

  saveEstablishmentProgress(establishmentId: string, body: any, version?: number | null): Observable<void> {
    const url = `${this.base}/establishments/${encodeURIComponent(establishmentId)}/progress`;
    let params = new HttpParams();
    if (version != null) params = params.set('version', String(version));
    return this.http.post<void>(url, body, { params });
  }

  loadEstablishmentProgress(establishmentId: string, version?: number | null): Observable<any | null> {
    const url = `${this.base}/establishments/${encodeURIComponent(establishmentId)}/progress`;
    let params = new HttpParams();
    if (version != null) params = params.set('version', String(version));
    return this.http.get<any>(url, { params }).pipe(
      map((v) => v ?? null),
      catchError(() => of(null))
    );
  }

  /* ============================== Local storage ============================== */
  // Nouvelle clé incluant wizardId (avec rétro-compat lecture de l’ancienne clé)
  private key(estId: string, version?: number | null, wizardId?: string | null) {
    const ver = version ?? 'na';
    return `${this.storagePrefix}${estId}__${wizardId ?? 'wiz'}__${ver}`;
  }
  // Ancienne clé (compat lecture)
  private legacyKey(estId: string, version?: number | null) {
    const ver = version ?? 'na';
    return `${this.storagePrefix}${estId}__${ver}`;
  }

  localSave(estId: string, version: number | null | undefined, payload: any, wizardId?: string | null) {
    try {
      const kNew = this.key(estId, version ?? null, wizardId ?? null);
      localStorage.setItem(kNew, JSON.stringify(payload));
      // Pour éviter toute perte et garder compat descendante, on duplique sur l’ancienne clé
      const kOld = this.legacyKey(estId, version ?? null);
      localStorage.setItem(kOld, JSON.stringify(payload));
    } catch {}
  }

  localLoad(estId: string, version: number | null | undefined, wizardId?: string | null): any | null {
    try {
      const kNew = this.key(estId, version ?? null, wizardId ?? null);
      const rawNew = localStorage.getItem(kNew);
      if (rawNew) return JSON.parse(rawNew);

      const kOld = this.legacyKey(estId, version ?? null);
      const rawOld = localStorage.getItem(kOld);
      return rawOld ? JSON.parse(rawOld) : null;
    } catch {
      return null;
    }
  }

  /* ============================== Helpers ============================== */

  private normalizeDefaults(src: WizardDefaultsDto | undefined | null): WizardDefaultsDto {
    const wizardId = src?.wizardId ?? 'establishment-setup';
    const version = typeof src?.version === 'number' || src?.version === null ? src!.version : null;
    const label = src?.label ?? null;

    const payloadDefault = (src && (src as any).payloadDefault)
      ? (src as any).payloadDefault
      : (src && (src as any).payload)
        ? (src as any).payload
        : {};

    return { wizardId, version, label, payloadDefault };
  }
}
